import * as Redux from 'redux';
import { Decimal } from 'decimal.js';

import { WizardPageEnum } from './interfaces/wizard-page';

import { ImportEvent, ImportStatus } from './ImportEventsReducer';
import { WebPortalClient } from 'webportalclient';
import { useWebPortalApiClient } from '../../misc/api/WebPortalTokenClient';

export enum ActionType {
    OpenImportEventsDialog = 'OpenImportEventsDialog',
    CloseImportEventsDialog = 'CloseImportEventsDialog',
    SelectAnalysisSet = 'SelectAnalysisSet',
    SetEventsFile = 'SetEventsFile',
    JumpToFileSelection = 'JumpToFileSelection',
    JumpToConfirmation = 'JumpToConfirmation',
    JumpToImporting = 'JumpToImporting',
    JumpToImportSummary = 'JumpToImportSummary',
    JumpToFinish = 'JumpToFinish',
    SetEventsList = 'SetEventsList',
    ImportEventAsync = 'ImportEventAsync',
    StartImportEvent = 'StartImportEvent',
    FinishImportEvent = 'FinishImportEvent',
    Cancel = 'Cancel',
    OtherAction = "OtherAction",
    ErrorImportingEvent = "ErrorImportingEvent"
}

declare var rootUrl: string;

export class CloseImportEventsDialogAction implements Redux.Action {
    type: ActionType.CloseImportEventsDialog;

    static create(): CloseImportEventsDialogAction {
        return {
            type: ActionType.CloseImportEventsDialog
        };
    }
}

export class SelectAnalysisSetAction implements Redux.Action {
    type: ActionType.SelectAnalysisSet;
    analysisSetId: string;

    static create(analysisSetId: string): SelectAnalysisSetAction {
        return {
            type: ActionType.SelectAnalysisSet,
            analysisSetId: analysisSetId,
        }
    }
}

export class JumpToFileSelectionAction implements Redux.Action {
    type: ActionType.JumpToFileSelection;
    currentPage: WizardPageEnum;
    nextAction: typeof JumpToConfirmationAction;
    backAction: typeof CancelAction;

    static create(): JumpToFileSelectionAction {
        return {
            type: ActionType.JumpToFileSelection,
            currentPage: WizardPageEnum.FileSelection,
            nextAction: JumpToConfirmationAction,
            backAction: CancelAction
        }
    }
}

export class JumpToConfirmationAction implements Redux.Action {
    type: ActionType.JumpToConfirmation;
    currentPage: WizardPageEnum;
    nextAction: typeof JumpToImportingAction;
    backAction: typeof JumpToFileSelectionAction;

    static create(): JumpToConfirmationAction {
        return {
            type: ActionType.JumpToConfirmation,
            currentPage: WizardPageEnum.Confirmation,
            nextAction: JumpToImportingAction,
            backAction: JumpToFileSelectionAction
        }
    }
}

export class JumpToImportingAction implements Redux.Action {
    type: ActionType.JumpToImporting;
    currentPage: WizardPageEnum;
    nextAction: typeof JumpToImportSummaryAction;
    backAction: null;

    static create(): JumpToImportingAction {
        return {
            type: ActionType.JumpToImporting,
            currentPage: WizardPageEnum.Importing,
            nextAction: JumpToImportSummaryAction,
            backAction: null
        }
    }
}

export class JumpToImportSummaryAction implements Redux.Action {
    type: ActionType.JumpToImportSummary;
    currentPage: WizardPageEnum;
    nextAction: typeof JumpToFinishAction;
    backAction: null;

    static create(): JumpToImportSummaryAction {
        return {
            type: ActionType.JumpToImportSummary,
            currentPage: WizardPageEnum.ImportSummary,
            nextAction: JumpToFinishAction,
            backAction: null
        }
    }
}

export class JumpToFinishAction implements Redux.Action {
    type: ActionType.JumpToFinish;
    currentPage: WizardPageEnum;

    static create(): JumpToFinishAction {
        return {
            type: ActionType.JumpToFinish,
            currentPage: WizardPageEnum.Finish
        };
    }
}

export class CancelAction implements Redux.Action {
    type: ActionType.Cancel;
    currentPage: WizardPageEnum;

    static create(): CancelAction {
        return {
            type: ActionType.Cancel,
            currentPage: WizardPageEnum.Cancel
        };
    }
}

export class SetEventsFileAction implements Redux.Action {
    type: ActionType.SetEventsFile;
    filename: string;

    static create(filename: string): SetEventsFileAction {
        return {
            type: ActionType.SetEventsFile,
            filename: filename
        }
    }
}

export class SetEventsListAction implements Redux.Action {
    type: ActionType.SetEventsList;
    eventsList: Array<ImportEvent>;

    static create(eventsList: Array<ImportEvent>): SetEventsListAction {
        return {
            type: ActionType.SetEventsList,
            eventsList: eventsList
        }
    }
}

export class ImportEventAsyncAction implements Redux.Action {
    type: ActionType.ImportEventAsync;

    static create(eventList: Array<ImportEvent>, analysisSetId: string, secondsOffset: number, eventTypeId: string, client: WebPortalClient) {
        return async (dispatch) => {
            const events = eventList.map(x => { return { Id: x.Id, Position: x.Position + secondsOffset, IsImporting: x.IsImporting } });

            const promises = [];
            for (let i = 0; i < events.length; i++) {
                dispatch(StartImportEventAction.create(events[i]));
                promises.push(this.addEvent(dispatch, events[i], analysisSetId, secondsOffset, eventTypeId, client));
            }

            return Promise.all(promises);
        };
    }

    static addEvent(dispatch, event: ImportEvent, analysisSetId: string, secondsOffset: number, eventTypeId: string, client: WebPortalClient): Promise<any> {
        return client.postCoughAnalysisSetEvent({
            analysisSetId: analysisSetId,
            eventTypeId: eventTypeId,
            rawStart: event.Position,
        })
            .then(json => {
                dispatch(FinishImportEventAction.create(json, event, secondsOffset));
            })
            .catch(err => {
                //An error occurred when importing a tag. Need to mark the tag as failed.
                dispatch(ErrorImportingEventAction.create(event));
            });
    }
}

export class StartImportEventAction implements Redux.Action {
    type: ActionType.StartImportEvent;
    eventValue: ImportEvent;

    static create(eventValue: ImportEvent): StartImportEventAction {
        return {
            type: ActionType.StartImportEvent,
            eventValue: eventValue
        }
    }
}


export interface ImportEventResult {
    IsSuccess: boolean;
    Message: string;
    RawEventStartTimes: Array<ImportEvent>;
}

export class FinishImportEventAction implements Redux.Action {
    type: ActionType.FinishImportEvent;
    eventValue: ImportEvent;

    static create(serverEvent: any, clientEvent: ImportEvent, secondsOffset: number): FinishImportEventAction {
        let precisionEventTime = new Decimal(serverEvent.rawStart);
        let precisionSecondsOffset = new Decimal(secondsOffset);
        let position = precisionEventTime.sub(precisionSecondsOffset);

        return {
            type: ActionType.FinishImportEvent,
            eventValue: { Id: clientEvent.Id, Position: position.toNumber(), IsImporting: ImportStatus.Imported },
        }
    }
}

export class ErrorImportingEventAction implements Redux.Action {
    type: ActionType.ErrorImportingEvent;
    eventValue: ImportEvent;

    static create(clientEvent: ImportEvent): ErrorImportingEventAction {
        return {
            type: ActionType.ErrorImportingEvent,
            eventValue: { Id: clientEvent.Id, Position: -1, IsImporting: ImportStatus.FailedImport }
        };
    }
}

export class OtherAction implements Redux.Action {
    type: ActionType.OtherAction;

    static create(): OtherAction {
        return {
            type: ActionType.OtherAction
        }
    }
}

export type Action = CloseImportEventsDialogAction | SelectAnalysisSetAction | SetEventsFileAction | JumpToFileSelectionAction | JumpToConfirmationAction | JumpToImportingAction | JumpToImportSummaryAction | JumpToFinishAction | CancelAction | SetEventsListAction | StartImportEventAction | FinishImportEventAction | OtherAction | ErrorImportingEventAction;
